// Keeps a page alive while the browser translates it (JYNIO-WEB-E, -WEB-M).
// Chrome's and Edge's translators replace each text node React rendered with
// <font> wrappers holding the translation, and sometimes move React's elements
// inside them. React still points at the original nodes, so its next
// removeChild, or insertBefore in front of one, throws NotFoundError, and the
// ErrorBoundary swaps the whole page for "Something went wrong". Reloading does
// not help: the translator comes straight back. React has left this open for
// years (facebook/react#11538); this is the usual workaround, made to keep the
// DOM as close as possible to what React meant.
//
// Only the impossible calls change; a node that really is a child of the
// parent goes through the browser's own method untouched.

export type DomTranslationGuardOp = "removeChild" | "insertBefore";

// Symbol.for, so a second copy of this module (HMR) still sees the first install.
const ORIGINALS = Symbol.for("jynio.domTranslationGuard");

type Originals = { removeChild: Node["removeChild"]; insertBefore: Node["insertBefore"] };
type GuardedPrototype = Node & { [ORIGINALS]?: Originals };

export function installDomTranslationGuard(onHit?: (op: DomTranslationGuardOp) => void): void {
  // The SSR sidecar renders in Node.js, where there is no DOM to guard.
  if (typeof Node !== "function") return;
  const proto = Node.prototype as GuardedPrototype;
  if (proto[ORIGINALS]) return;
  const { removeChild, insertBefore } = proto;
  proto[ORIGINALS] = { removeChild, insertBefore };

  proto.removeChild = function guardedRemoveChild<T extends Node>(this: Node, child: T): T {
    if (!child || child.parentNode === this) return removeChild.call(this, child) as T;
    onHit?.("removeChild");
    // Moved into the translator's wrapper, but still inside this parent: React
    // wants it gone, and leaving it would keep a dead copy on screen. A
    // replaced text node is detached already, so there is nothing to remove.
    if (child.parentNode && this.contains(child)) removeChild.call(child.parentNode, child);
    return child;
  };

  proto.insertBefore = function guardedInsertBefore<T extends Node>(this: Node, node: T, ref: Node | null): T {
    if (!ref || ref.parentNode === this) return insertBefore.call(this, node, ref) as T;
    onHit?.("insertBefore");
    // Insert before the wrapper that now holds the reference node, so a spinner
    // stays in front of its label. A detached reference leaves no trace of its
    // position; the null anchor then appends.
    let anchor: Node | null = ref;
    while (anchor && anchor.parentNode !== this) anchor = anchor.parentNode;
    return insertBefore.call(this, node, anchor) as T;
  };
}

// Test hook: restores the browser's own methods.
export function uninstallDomTranslationGuard(): void {
  if (typeof Node !== "function") return;
  const proto = Node.prototype as GuardedPrototype;
  const originals = proto[ORIGINALS];
  if (!originals) return;
  proto.removeChild = originals.removeChild;
  proto.insertBefore = originals.insertBefore;
  delete proto[ORIGINALS];
}
